import { useRef, useState } from 'react';
import styled from '@emotion/styled';
import { SelectInput } from '../../../../components/common/select/SelectInput';
import {
	Css,
	IDropdownItem,
	ITheme,
	Responsive,
	StyledSelectMain,
} from '../../../../ui';
import {
	IPoiFilterType,
	PoiFilterOption,
	mapPoiFilterTypeToFilterItems,
	poiFilterOptions,
} from '../domain/models/PoiFilterOption';
import { ALL_LOCATIONS_FILTER_RESET } from '../../domain/utils/getAllTerminalLocationItems';
import { PoiOpeningHours } from '../domain/models/PoiOpeningHours';
import { PoiSecurityZoneItems } from '../domain/models/PoiSecurityZone';
import { ALL_POI_TYPES_FILTER_RESET } from '../../domain/utils/getPoiTypeKeywords';

interface IShopDineRelaxFiltersProps {
	poiTypeKeywords: string[];
	locationItems: string[];
	onSelectFilter: (
		filterType: PoiFilterOption,
		selectedItem: IPoiFilterType,
	) => void;
}

export default function ShopDineRelaxFilters({
	poiTypeKeywords,
	locationItems,
	onSelectFilter,
}: IShopDineRelaxFiltersProps) {
	const isMobileXL = Responsive.useMatchesBreakpoint('mobileXL');
	const [showPlaceholder, setShowPlaceholder] = useState<{
		[key: string]: boolean;
	}>({});

	const filterRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

	const resetAllOptions = [
		ALL_LOCATIONS_FILTER_RESET,
		PoiOpeningHours.AllHours,
		PoiSecurityZoneItems.All,
		ALL_POI_TYPES_FILTER_RESET,
	];

	const handleSelectFilter = (
		filterOption: PoiFilterOption,
		selectedItem: IPoiFilterType,
	) => {
		onSelectFilter(filterOption, selectedItem);
		setShowPlaceholder((prevState) => ({
			...prevState,
			[filterOption]: true && !resetAllOptions.includes(selectedItem),
		}));
	};

	// Scroll select filter into view when it's in a horizontal scroll
	const handleOnSelectClick = (index: number) => {
		const filterElement = filterRefs.current[index];
		const parentElement = filterElement?.parentElement;

		if (!parentElement) return;

		const parentRect = parentElement.getBoundingClientRect();
		const filterRect = filterElement.getBoundingClientRect();

		const { scrollLeft } = parentElement;
		const targetScrollLeft =
			scrollLeft + filterRect.left - parentRect.left - 16;

		parentElement.scrollTo({
			left: targetScrollLeft,
			behavior: 'smooth',
		});
	};

	return (
		<ShopDineRelaxFiltersContainer>
			<ShopDineRelaxFiltersTitle>Filter Options</ShopDineRelaxFiltersTitle>
			<ShopDineRelaxFiltersWrap>
				{poiFilterOptions.map((filterOption, index) => (
					<div
						key={filterOption}
						ref={(el) => {
							filterRefs.current[index] = el;
						}}
					>
						<StyledSelectInput
							key={filterOption}
							items={mapPoiFilterTypeToFilterItems(
								filterOption,
								poiTypeKeywords,
								locationItems,
							)}
							placeholder={filterOption}
							placeholderAsLabel={!!showPlaceholder[filterOption]}
							initialSelectedItem={poiFilterOptions[index]}
							withDropdownFloatingPortal={isMobileXL}
							onClick={() => handleOnSelectClick(index)}
							onChange={(selectedItem: IDropdownItem | null) =>
								handleSelectFilter(filterOption, selectedItem as IPoiFilterType)
							}
						/>
					</div>
				))}
			</ShopDineRelaxFiltersWrap>
		</ShopDineRelaxFiltersContainer>
	);
}

const ShopDineRelaxFiltersContainer = styled.div`
	min-width: 100vw;
`;

const ShopDineRelaxFiltersWrap = styled.div<{}, ITheme>`
	display: flex;
	gap: ${Css.pxToRem(8)};

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		overflow-x: auto;
		overflow-y: hidden;
		margin-left: -${(props) => props.theme.layout.main.margin.mobile};
		padding-left: ${(props) => props.theme.layout.main.margin.mobile};
		padding-right: ${(props) => props.theme.layout.main.margin.mobile};
		width: 100%;
	}
`;

const ShopDineRelaxFiltersTitle = styled.h5`
	margin-top: ${Css.pxToRem(32)};
	margin-bottom: ${Css.pxToRem(16)};
`;

const StyledSelectInput = styled(SelectInput)`
	${StyledSelectMain} {
		min-width: 140px;
	}
`;
